import React, { useEffect, useState, memo, useCallback, useRef } from "react";
import {
  Dialog,
  CircularProgress,
  TextField,
  Box,
  Button,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import barcodeGif from "../../assets/images/barCodeImage.gif";
import barcodeIcon from "../../assets/images/scan-icon.png";
import Message from "./Message";

const LoadingDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiPaper-root": {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "transparent",
  },
  "& .MuiBackdrop-root": {
    backgroundColor: "#4848485c",
  },
}));

let nextId = 0;
const ScanDialog = ({ label, fieldValue, handleValue }) => {
  const [scanFieldDisabled, setScanFieldDisabled] = useState({
    scanFromButton: false,
    scanTextField: false,
    isScanFormButtonClick: false,
    isScanTextFieldActive: false,
  });
  const [openScanDialog, setOpenScanDialog] = useState(false);
  const [loadMessage, setLoadMessage] = useState("");

  const [code, setCode] = useState([]);
  const [reading, setReading] = useState(false);
  const [type, setType] = useState("");

  //******************************Scan Functions START ******************************//

  const handleScanEvent = (value) => {
    if (value.length > 0) {
      setType("success");
      setLoadMessage("Scanned successfully");
      let scannedValue = value.map((item) => item.name).join("");
      handleValue(scannedValue, true);
      setScanFieldDisabled({
        scanFromButton: false,
        scanTextField: true,
      });
      setOpenScanDialog(false);
    } else {
      setType("error");
      setLoadMessage("PO numbers are not scanned. Please try again!");
      setScanFieldDisabled({
        scanFromButton: false,
        scanTextField: false,
        isScanFormButtonClick: false,
      });
    }
  };
  const handleGlobalScan = (event) => {
    event.preventDefault();
    if (event.keyCode === 13) {
      /// code ready to use
      if (scanFieldDisabled.isScanFormButtonClick) {
        handleScanEvent(code);
      }
    } else {
      setCode([...code, { id: nextId++, name: event.key }]);
    }

    if (!reading) {
      setReading(true);
      setTimeout(() => {
        setReading(false);
      }, 200);
    }
  };

  //============================= Open Scanner======================================
  useEffect(() => {
    if (openScanDialog) {
      document.addEventListener("keypress", handleGlobalScan);
      return () => {
        document.removeEventListener("keypress", handleGlobalScan);
      };
    }
  }, [handleGlobalScan, openScanDialog]);
  // ================================Close Scanner==================================

  //****************************** Scan Functions END ******************************//
  // ###################################################################################
  // ###################################################################################
  // ###################################################################################

  //****************************** Paste Functions START ******************************//
  const handlePasteEvent = useCallback(
    (value) => {
      if (value && value.trim().length > 0) {
        setType("success");
        setLoadMessage("Scanned successfully");
        handleValue(value);
        setScanFieldDisabled({
          scanFromButton: false,
          scanTextField: true,
        });
        setOpenScanDialog(false);
      } else {
        setType("error");
        setLoadMessage("PO numbers are not scanned. Please try again!");
        setScanFieldDisabled({
          scanFromButton: false,
          scanTextField: false,
          isScanFormButtonClick: false,
        });
      }
    },
    [handleValue]
  );

  const handleGlobalPaste = useCallback(
    (event) => {
      const pastedText = event.clipboardData.getData("text/plain");
      if (scanFieldDisabled.isScanFormButtonClick) {
        handlePasteEvent(pastedText);
      }
    },
    [handlePasteEvent, scanFieldDisabled.isScanFormButtonClick]
  );

  //============================= open Paste ======================================
  useEffect(() => {
    document.addEventListener("paste", handleGlobalPaste);
    return () => {
      document.removeEventListener("paste", handleGlobalPaste);
    };
  }, [handleGlobalPaste]);
  // ================================close Paste ==================================

  //****************************** Paste Functions END ******************************//
  // ###################################################################################
  // ###################################################################################
  // ###################################################################################

  useEffect(() => {
    document.addEventListener("dblclick", handleCloseScanerLoader);
    document.addEventListener("keydown", handleCloseScanerLoader, true);
    return () => {
      document.removeEventListener("dblclick", handleCloseScanerLoader);
      document.removeEventListener("keydown", handleCloseScanerLoader, true);
    };
  }, [scanFieldDisabled.isScanFormButtonClick]);

  // =================Get value using inputText Field =======================

  const handleChangeTextField = useCallback(
    (event) => {
      const { value } = event.target;
      handleValue(value);
      if (value.length > 0) {
        setScanFieldDisabled({
          scanFromButton: true,
          scanTextField: false,
        });
      } else {
        setScanFieldDisabled({
          scanFromButton: false,
          scanTextField: false,
        });
      }
    },
    [handleValue]
  );

  const handleCloseScanerLoader = (event) => {
    if (
      (event.type === "keydown" && event.code === "Escape") ||
      event.type === "dblclick"
    ) {
      if (scanFieldDisabled.isScanFormButtonClick) {
        setOpenScanDialog(false);
      }
    }
  };
  const handleOpenScanvalue = useCallback(() => {
    nextId = 0;
    setCode([]);
    setOpenScanDialog(true);
    setLoadMessage("");
    setScanFieldDisabled((state) => ({
      ...state,
      isScanFormButtonClick: true,
    }));
  }, []);

  const { scanFromButton, scanTextField } = scanFieldDisabled;
  

  return (
    <>
      <Box sx={{ width: "100%" }}>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            gap: "3px",
            flexWrap: "wrap",
            paddingBottom: "10px",
          }}
        >
          <Button
            sx={{
              "&:hover": {},
              order: {
                xs: 2,
                sm: 1,
              },
            }}
            disabled={scanFromButton}
            onClick={handleOpenScanvalue}
            startIcon={
              <img
                src={barcodeIcon}
                alt="barcodeIcon"
                style={{
                  borderRadius: "10px",
                  maxWidth: "60px",
                  height: "35px",
                }}
              />
            }
          >
            {/* {label} */}
          </Button>
          <Box
            flexGrow={1}
            sx={{
              order: {
                xs: 1,
                sm: 2,
              },
            }}
          >
            <TextField
              type="text"
              label={label}
              size="small"
              autoComplete="off"
              fullWidth
              disabled={scanTextField}
              name={`random_${Math.random().toString(36).substring(2, 9)}`}
              onChange={handleChangeTextField}
              // inputRef={textFieldRef}
              autoFocus
            />
          </Box>
        </Box>

        {fieldValue && fieldValue.trim().length > 0 && (
          <Box
            sx={{
              paddingLeft: {
                xs: "0px",
                sm: "67px",
              },
              paddingRight: {
                xs: "67px",
                sm: "0px",
              },
            }}
          >
            <TextField
              label={label}
              size="small"
              fullWidth
              disabled
              value={fieldValue}
            />
          </Box>
        )}
      </Box>

      <LoadingDialog fullScreen open={openScanDialog}>
        <img
          src={barcodeGif}
          alt="barcode"
          style={{
            borderRadius: "10px",
            width: "400px",
            maxHeight: "150px",
          }}
        />
      </LoadingDialog>
      <Message type={type} msg={loadMessage} />
    </>
  );
};

export default memo(ScanDialog);
